import { FileMutationDto, IFileDto } from '../../config/fileConfig'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { ModuleType } from '../../context/GlobalContext'

export type FileModuleDto = {
    id?: string
    type: ModuleType
}

export type InsolvencyModuleDto = FileModuleDto & {
    factorEstate?: string
    rateEstate?: string
}

export type BudgetMonitoringModuleDto = FileModuleDto & {
    hourThreshold: number
    priceThreshold: string
    includeHours: boolean
    includeTravels: boolean
    includeDisbursements: boolean
    includeOfferedHours: boolean
    includeBilled: boolean
}

export type WorkInProgressModuleDto = FileModuleDto & {
    percentage?: string
    maxPrice?: string
}

export type FileTypes = FileMutationDto | IFileDto

export type UseFileModuleType<ModuleT = FileModuleDto> = {
    has: boolean
    index: number
    fileModule?: ModuleT
    setCurrentFile: Dispatch<SetStateAction<FileTypes>>
}

export type UseFileModulesType<ModuleT> = {
    has: (type: ModuleType) => boolean
    index: (type: ModuleType) => number
    fileModule?: (type: ModuleType) => ModuleT
    hasFile: () => boolean
    setCurrentFile: (file: FileTypes) => void
}

export function useFileModule<ModuleT = FileModuleDto>(moduleType: ModuleType, initialFile?: FileTypes): UseFileModuleType<ModuleT> {
    const filter = (module: FileModuleDto): boolean => module.type === moduleType
    const [has, setHas] = useState<boolean>()
    const [index, setIndex] = useState<number>()
    const [fileModule, setFileModule] = useState<FileModuleDto>()
    const [currentFile, setCurrentFile] = useState<FileTypes>(undefined)

    useEffect(() => {
        setHas(currentFile?.modules?.some(filter) || false)
        setFileModule(currentFile?.modules?.find(filter))
        setIndex(currentFile?.modules?.findIndex(filter))
    }, [currentFile])

    useEffect(() => {
        setCurrentFile(initialFile)
    }, [])

    return { has, index, fileModule: fileModule as ModuleT, setCurrentFile }
}

export function useFileModules<ModuleT = FileModuleDto>(initialFile?: FileTypes): UseFileModulesType<ModuleT> {
    const [currentFile, setCurrentFile] = useState<FileTypes>(initialFile)

    const filter =
        (type: ModuleType) =>
        (module: FileModuleDto): boolean =>
            module.type === type

    return {
        has: (type: ModuleType) => currentFile?.modules?.some(filter(type)),
        index: (type: ModuleType) => currentFile?.modules?.findIndex(filter(type)),
        fileModule: (type: ModuleType) => currentFile?.modules?.find(filter(type)) as ModuleT,
        hasFile: () => currentFile !== undefined,
        setCurrentFile,
    }
}
